import React, { ReactElement, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { withRouter } from 'react-router'

import { IonGrid, IonRow, IonFooter } from '@ionic/react'

import {
	TourStopsContainer as Container,
	CollectionItem as CollectionItemType,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	INITIAL_LAYOUT_MAP,
	KeypadEntryStatus,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_MENU,
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_TYPE_SCREEN,
	RootStoreState,
	Collection,
} from 'mytours-read-common'

import { CollectionItemsNavigationParams, BoundDirection, CollectionItemsRouteParams } from '../types'

import Button from '../../common/components/Button'

import { ionHistory } from '../../navigation'
import { getTabsUrl } from '../../navigation/routes'
import NavigationService from '../../navigation/NavigationService'
import MenuList from '../../common/components/menu/MenuList'

import Page from '../../common/components/Page'
import { Props as HeaderProps } from '../../common/components/header'

import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageUrl } from 'common_lib/utils'
import usePrevious from 'common_lib/hooks/use-previous'
import { EventActionsEnum, sendEvent } from 'mobile_web/modules/logging/analytics'
import { GetCollectionItemsIndexResponseData, RedeemCode, MapRoute } from 'mytours-read-api-typescript'
import { validateCollectionCode } from 'mobile_web/modules/lockapp/functions'
import ScannerModal from 'mobile_web/modules/scanner/components/ScannerModal'
import MetaHeader from '../parts/MetaHeader'
import TourStopFooter from '../parts/tour-stop-footer'
import MapLayout from 'mobile_web/modules/map/parts/MapActionsLayout'
import Modal from 'mobile_web/modules/common/components/modal'
import { loadingTimeout } from 'mobile_web/modules/common/functions'
import StoryContentPlaceholder from 'mobile_web/modules/common/components/placeholders/StoryContent'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'
import DefaultPagePlaceholder from 'mobile_web/modules/common/components/placeholders/DefaultPage'
import { getPlaceholderTypeByPathname } from 'mobile_web/modules/common/components/placeholders/functions'
import Content from 'mobile_web/modules/common/components/content'

import strings from '../../../strings'

import classNames from 'classnames'
import Page404 from 'mobile_web/404'
import './styles/collectionItems.less'
import CollectionListComponent from './CollectionList'
import ScreenStoryComponent from 'mobile_web/modules/screen/components/ScreenStory'
import ScreenPanoramaComponent from 'mobile_web/modules/screen/components/ScreenPanorama'
import ScreenWebComponent from 'mobile_web/modules/screen/components/ScreenWeb'
import CollectionTourComponent from './CollectionTour'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import LockAppComponent from 'mobile_web/modules/lockapp/components/LockApp'

type Props = Container.Props & Container.Actions & CollectionItemsRouteParams & CollectionItemsNavigationParams

const CollectionItems: React.FC<Props> = props => {
	const [itemVersion, setItemVersion] = useState<number>(0)
	const [locationHash, setLocationHash] = useState<string | undefined>()
	const [opennedMapModalByHash, setOpennedMapModalByHash] = useState<boolean>(false)

	const isTour = props.match.params.type === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour

	const showMapLayout = useMemo<boolean>(() => {
		const { collection = {} as Collection } = props
		const { initial_layout: initialLayout } = collection

		return initialLayout === INITIAL_LAYOUT_MAP
	}, [props.collection?.initial_layout])

	const collectionItems = useMemo<CollectionItemType[]>(() => {
		if (!isTour) {
			return props.collectionItems
		}

		return props.collectionItems?.filter(item => item.item_type === COLLECTION_ITEM_TYPE_SCREEN)
	}, [props.collectionItems])

	const [showModal, setShowModal] = useState<boolean>(false)
	const [showScanner, setShowScanner] = useState<boolean>(false)
	const [mapModalDidPresent, setMapModalDidPresent] = useState<boolean>(false)
	const [collectionItemIndex, setCollectionItemIndex] = useState<number>(null)
	const [codeLock, setCodeLock] = useState<boolean>(null)
	const [codeValidationStarted, setCodeValidationStarted] = useState<boolean>(false)
	const [isMapModalLoading, setIsMapModalLoading] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(collectionItems === undefined)

	const activeItem = collectionItems?.[collectionItemIndex || 0]
	const activeScreen = useSelector((state: RootStoreState) => state.screen.screens.get(activeItem?.item_id))?.screen

	const sendSelectStopEvent = (method: 'keypad' | 'qr-code' | 'hamburger', index: number) => {
		const { collection } = props
		sendEvent(EventActionsEnum.SelectItem, {
			collection: collection?.name,
			collectionId: collection?.id,
			itemName: collectionItems?.[index]?.name,
			method,
		})
	}

	const getItemIndexById = (id: number): number => {
		const { match } = props
		const {
			params: { item: itemParam },
		} = match

		return collectionItems?.findIndex(
			// check `item_id` for tours stop; `id` for any collection item
			item => (itemParam === 'item' && item.id === id) || (item.item_subtype === itemParam && item.item_id === id),
		)
	}

	const getItemIdByIndex = (index: number): number | undefined => {
		const { match } = props

		if (!collectionItems[index]) {
			return
		}

		const {
			params: { item: itemParam },
		} = match

		if (itemParam === 'item') {
			// return `id` (CollectionItemId) for tours stop
			return collectionItems[index].id
		}

		// return `item_id` (ScreenId or CollectionId) for any collection item
		return collectionItems[index].item_id
	}

	const setCurrentPage = (pageIndex: number, stateToUpdate: Record<string, string | number> = {}) => {
		const { collectionId, collectionVersion, collectionTitle, title, collectionAccountId } = props

		const queryParams = {}

		if (stateToUpdate.action === 'menu' && (showMapLayout || showModal)) {
			queryParams['show-detail-modal'] = 'true'
		}

		NavigationService.navigateToCollectionItems({
			params: {
				collectionId,
				collectionVersion,
				itemId: getItemIdByIndex(pageIndex),
				collectionTitle,
				title,
				collectionAccountId,
			},
			collectionType: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour,
			itemType: 'item',
			stateToUpdate,
			queryParams,
			isOnlyChangePage: true,
		})
	}

	const getPageNumber = (): number => {
		const {
			match: { params },
		} = props

		if (!params || (params && (!params.itemId || !params.item))) {
			return params.type === 'tour' ? 0 : -1
		}

		return getItemIndexById(Number(params.itemId))
	}

	const changeStopByItemId = (itemId: number) => {
		const collectionItemIndex = getItemIndexById(itemId)

		if (collectionItemIndex > -1) {
			sendSelectStopEvent('keypad', collectionItemIndex)
			setCurrentPage(collectionItemIndex)
			setShowScanner(false)
		}
	}

	useLayoutEffect(() => {
		if (ionHistory?.location?.hash) {
			// handle openning stop from qr code scanner
			if (ionHistory?.location?.hash === '#qr-code') {
				setLocationHash('qr-code')
				ionHistory.replace(ionHistory?.location?.pathname)
			}
		}
	}, [])

	useEffect(() => {
		if (!props.collectionVersion) return
		if (collectionItems === undefined) {
			setItemVersion(props.collectionVersion)
			props.downloadTour(props.collectionVersion, props.language, 'tour', props.collectionAccountId)
		}

		props.loadCollectionDetails(props.collectionId, props.collectionVersion, props.language, props.collectionAccountId)
	}, [props.collectionVersion])

	useEffect(() => {
		let prevHash = ionHistory.location.hash
		const unListenHistory = ionHistory.listen(({ hash }) => {
			// Close map modal when press back button
			if (prevHash === '#tour-map' && hash === '') {
				setShowModal(false)
			}

			prevHash = hash
		})

		return () => {
			unListenHistory()
		}
	}, [])

	useEffect(() => {
		if (props.collection?.version && props.collection?.version != itemVersion) {
			setItemVersion(props.collection?.version)
			props.downloadTour(props.collection?.version, props.language, 'tour', props.collectionAccountId)
		}
	}, [props.collection?.version, itemVersion])

	const prevPropsMatchParams: any = usePrevious(props.match.params)
	useEffect(() => {
		if (
			collectionItems &&
			(prevPropsMatchParams?.itemId != props.match.params?.itemId || collectionItemIndex === null)
		) {
			setCollectionItemIndex(getPageNumber())
		}
	}, [collectionItems, prevPropsMatchParams?.itemId, props.match.params?.itemId, collectionItemIndex])

	const prevPropsKeypadEntryStatus: KeypadEntryStatus = usePrevious(props.keypadEntryStatus)
	useEffect(() => {
		if (
			prevPropsKeypadEntryStatus === KeypadEntryStatus.Processing &&
			!props.keypadEntryStatus &&
			props.keypadEntryItems.length === 1 &&
			!props.keypadEntryLoading &&
			props.collection.initial_layout !== INITIAL_LAYOUT_MAP
		) {
			changeStopByItemId(props.keypadEntryItems[0].id)
		}
	}, [prevPropsKeypadEntryStatus, props.keypadEntryStatus, props.keypadEntryItems, props.keypadEntryLoading])

	useEffect(() => {
		if (locationHash === 'qr-code' && props.collection && collectionItems) {
			setLocationHash(undefined)
			sendSelectStopEvent('qr-code', getPageNumber())
		}
	}, [locationHash, props.collection, collectionItems])

	useEffect(() => {
		const isEmpty: boolean = !!collectionItems && collectionItems.length === 0

		if (
			!opennedMapModalByHash &&
			ionHistory.location.hash === '#tour-map' &&
			!loading &&
			!isEmpty &&
			!showModal &&
			props.hasMap &&
			!showMapLayout
		) {
			setOpennedMapModalByHash(true)
			setIsMapModalLoading(true)
			// open modal in 3 sec after hash matches, because we can't know when page is fully loaded
			// TODO: add callback after page loaded
			setTimeout(() => {
				setShowModal(true)
				setIsMapModalLoading(false)
			}, 3000)
		}
	}, [
		collectionItems,
		opennedMapModalByHash,
		ionHistory.location.hash,
		loading,
		showModal,
		props.hasMap,
		showMapLayout,
	])

	useEffect(() => {
		if (!props.collection) return

		if (codeLock === null && props.collection?.hasOwnProperty('code_lock')) {
			// TODO: add additional condition to check if code is loading
			setCodeLock(props.collection?.code_lock)
		}
	}, [props.collection?.code_lock])

	const prevStateCodeLock: boolean = usePrevious(codeLock)
	useEffect(() => {
		if (prevStateCodeLock === codeLock) return
		;(async () => {
			if (!codeValidationStarted && props.collection?.code_lock) {
				setCodeValidationStarted(true)
				await validateCollectionCode(props.collection, props.validateCode)
			}
		})()
	}, [codeLock])

	const prevPropsLockCollection: any = usePrevious(props.lockCollection.status)
	useEffect(() => {
		const { lockCollection, collection, validateCode } = props
		if (!lockCollection.loading && lockCollection.loaded) {
			if (lockCollection.status === 'valid' && !prevPropsLockCollection) {
				setCodeLock(false)
			} else if (
				(lockCollection.status === 'invalid' || lockCollection.status === 'expired') &&
				!prevPropsLockCollection
			) {
				validateCollectionCode(collection, validateCode)
			}
		}
	}, [props.lockCollection, props.collection])

	useEffect(() => {
		const isLoading: boolean = collectionItems === undefined || props.lockCollection?.loading

		if (isLoading !== loading) {
			// loading with timeout to change skeleton with images content smoothly
			loadingTimeout(isLoading, val => setLoading(val))
		}
	}, [collectionItems, props.lockCollection.loading, loading])

	const renderItem = (collectionItem: CollectionItemType = {}, isDirectRender: boolean): ReactElement => {
		const { item_subtype: itemSubtype, item_id: itemId, account_id: accountId, version } = collectionItem
		const { collection = {} as Collection } = props
		switch (itemSubtype) {
			case COLLECTION_ITEM_SUBTYPE_WEB:
				return <ScreenWebComponent itemId={itemId} itemVersion={version} isPageContent={!isDirectRender} accountId={accountId} />
			case COLLECTION_ITEM_SUBTYPE_STORY:
				return (
					<ScreenStoryComponent
						itemId={itemId}
						itemVersion={version}
						isPageContent={!isDirectRender}
						collection={collection}
						accountId={accountId}
					/>
				)
			case COLLECTION_ITEM_SUBTYPE_PANORAMA:
				return (
					<ScreenPanoramaComponent
						itemId={itemId}
						itemVersion={version}
						isPageContent={!isDirectRender}
						collectionId={collection.id}
						accountId={accountId}
					/>
				)
			case COLLECTION_ITEM_SUBTYPE_LIST:
			case COLLECTION_ITEM_SUBTYPE_MENU:
				return <CollectionListComponent collectionId={itemId} collectionVersion={version} accountId={accountId} />
			case COLLECTION_ITEM_SUBTYPE_TOUR:
				return <CollectionTourComponent collectionId={itemId} collectionVersion={version} accountId={accountId} />
			default:
				return <div />
		}
	}

	const hasPrevious = useMemo<boolean>(() => collectionItemIndex > 0, [collectionItemIndex])

	const hasNext = useMemo<boolean>(
		() => !!collectionItems && collectionItemIndex < collectionItems.length - 1,
		[collectionItems, collectionItemIndex],
	)

	const onChooseStopFromMenu = (id: number) => {
		const pageIndex = getItemIndexById(id)
		sendSelectStopEvent('hamburger', pageIndex)
		setCurrentPage(pageIndex, { action: 'menu' })
	}

	const renderMenuContent = (): JSX.Element => {
		const activePage = collectionItems?.[collectionItemIndex]?.id

		return <MenuList list={collectionItems} activePageId={activePage} navigateToItem={onChooseStopFromMenu} />
	}

	const onTourExit = () => {
		// TODO: Property 'prevPagePath' does not exist on type 'unknown'
		const { state }: any = ionHistory.location
		const prevPagePath = state?.prevPagePath || getTabsUrl()

		ionHistory.replace(prevPagePath)
	}

	const toggleMapModal = () => {
		setShowModal(!showModal)
	}

	const changeScreenAction = (direction: BoundDirection) => {
		let index

		if (direction == 'prev') {
			index = collectionItemIndex - 1 > -1 ? collectionItemIndex - 1 : collectionItems.length - 1
		} else if (direction == 'next') {
			index = collectionItemIndex + 1 < collectionItems.length ? collectionItemIndex + 1 : 0
		}

		setCurrentPage(index)
	}

	const toggleMapModalDidPresent = (status: boolean) => {
		setOpennedMapModalByHash(true)
		setMapModalDidPresent(status)
		if (!status) {
			setShowModal(false)
			ionHistory.replace(ionHistory.location.pathname)
		} else if (!showMapLayout) {
			window.location.hash = '#tour-map'
		}
	}

	const toggleScannerModal = () => {
		setShowScanner(!showScanner)
	}

	const getHeaderProps = (): HeaderProps | undefined => {
		const { collection = {} as Collection, match } = props
		const { title = '' } = collection

		const {
			params: { type: collectionType },
		} = match

		const currentStop: CollectionItemType = collectionItems?.[collectionItemIndex]

		switch (collectionType) {
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour:
				return {
					menuTitle: title,
					type: showMapLayout ? 'hidden-menu' : 'menu',
					defaultScrolled: true,
					showTourExitButton: true,
					renderMenuContent,
					collection,
					buttonTextShadow: false,
					onBackButton: onTourExit,

					collectionItem: currentStop,
					screen: null,
					itemId: currentStop?.item_id,
					itemType: currentStop?.item_type,
					hideFavouriteButton: false,
				}
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List:
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu:
				// when `list` or `menu` then render screen component directly, without additional header

				return undefined
			default:
				return undefined
		}
	}

	const renderMapContent = ({ pageIndex, showMapLayoutParam }): JSX.Element => {
		const { collection = {} as Collection, collectionAccountId } = props
		const { keypad_enabled: keypadEnabled } = collection

		return (
			<MapLayout
				accountId={collectionAccountId}
				details={collection}
				items={collectionItems}
				currentItemIndex={pageIndex}
				onCloseMap={!showMapLayoutParam && toggleMapModal}
				setCurrentPage={setCurrentPage}
				isMapLayout={showMapLayoutParam}
				keypadEnabled={keypadEnabled}
				onExit={onTourExit}
				renderToggleMenuContent={renderMenuContent}
				itemType={MapRoute.LinkedTypeEnum.Collection}
				onSelectItemFromMenu={onChooseStopFromMenu}
			/>
		)
	}

	const renderModal = (): JSX.Element => {
		const currentStop: CollectionItemType = collectionItems?.[collectionItemIndex]

		if (!collectionItems || !currentStop) {
			return null
		}

		return (
			<Modal
				isOpen={showModal}
				cssClass="map-modal"
				backdropDismiss={false}
				showBackdrop={false}
				swipeToClose={false}
				onDidPresent={() => toggleMapModalDidPresent(true)}
				onDidDismiss={() => toggleMapModalDidPresent(false)}
			>
				<Content>
					{showMapLayout && (
						<IonRow className="close-map-button-container">
							<Button className="close-map-button list-view" onClick={toggleMapModal}>
								<i className="fal fa-xmark" />
							</Button>
						</IonRow>
					)}
					<IonGrid className={classNames('map-modal-content', showMapLayout && 'list-content')}>
						{showMapLayout
							? renderItem(currentStop, false)
							: mapModalDidPresent && renderMapContent({ pageIndex: collectionItemIndex, showMapLayoutParam: false })}
					</IonGrid>
				</Content>
				<IonFooter>{showModal && <AudioPlayerBarComponent bottomOffset={0} className="full-width" />}</IonFooter>
			</Modal>
		)
	}

	const renderScanner = () => {
		const { collection = {} as Collection } = props

		return (
			<ScannerModal
				qrEnabled={collection.qr_enabled}
				keypadEnabled={collection.keypad_enabled}
				showScanner={showScanner}
				toggleScannerModal={toggleScannerModal}
				itemId={collection.id}
			/>
		)
	}

	const renderPlaceholder = (): JSX.Element => {
		const placeholderStoryType = getPlaceholderTypeByPathname(ionHistory.location.pathname)

		return [COLLECTION_ITEM_SUBTYPE_PANORAMA, COLLECTION_ITEM_SUBTYPE_WEB].includes(placeholderStoryType) ||
			showMapLayout ? (
			<ImagePlaceholder style={{ height: '100%' }} />
		) : (
			<StoryContentPlaceholder style={{ height: 'calc(100% - 4px)', width: '100%', padding: '0 24px 24px' }} />
		)
	}

	const renderPageContent = (isEmpty: boolean) => {
		const activePage = collectionItems?.[collectionItemIndex]

		const isLoadingContent = loading || isMapModalLoading || codeLock
		const showContent = !isEmpty && collectionItems !== undefined && activePage

		if (isLoadingContent) return renderPlaceholder()

		return (
			<>
				{isEmpty && (
					<IonGrid className="empty-grid">
						<IonRow>{strings.no_stops}</IonRow>
					</IonGrid>
				)}
				{showContent &&
					(showMapLayout
						? renderMapContent({ pageIndex: collectionItemIndex, showMapLayoutParam: showMapLayout })
						: renderItem(activePage, false))}
				{renderScanner()}
			</>
		)
	}

	const { collection = {} as Collection, hasMap, lockCollection, match, error } = props

	const {
		title,
		description,
		code_title: codeTitle,
		code_text: codeText,
		code_background_media_item: codeBackgroundMediaItem,
		qr_enabled: qrEnabled,
		keypad_enabled: keypadEnabled,
	} = collection

	const {
		params: { type: collectionType, itemId },
	} = match

	const isEmpty: boolean = !!collectionItems && collectionItems.length === 0

	if (!loading && (collectionItemIndex === -1 || error?.status === 404)) {
		return <Page404 />
	}

	if (codeLock && !lockCollection.loading) {
		return (
			<LockAppComponent
				linkedId={collection.id}
				linkedType={RedeemCode.LinkedTypeEnum.Collection}
				title={codeTitle}
				description={codeText}
				backgroundImage={codeBackgroundMediaItem}
			/>
		)
	} else if (codeLock && lockCollection.loading) {
		return <DefaultPagePlaceholder isTourPage={true} />
	}

	const activePage = collectionItems?.[getItemIndexById(Number(itemId))]

	if (
		!loading &&
		activePage &&
		(collectionType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List ||
			collectionType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu)
	) {
		return renderItem(activePage, true)
	}

	const panoramaScreenClass =
		activePage?.item_subtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama && !showMapLayout
			? 'panorama-screen'
			: ''

	const activeItemSubtype = activeItem?.item_subtype
	const hasFullHeight =
		activeItemSubtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama ||
		activeItemSubtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web

	return (
		<>
			<MetaHeader title={title} description={description} pageEndIndex={collectionItems?.length} />
			<Page
				headerProps={{ ...getHeaderProps(), loading }}
				contentProps={{
					className: classNames(
						'tour-stops-page',
						panoramaScreenClass,
						showMapLayout && 'map-layout',
						hasFullHeight && 'full-height',
					),
					style:
						activePage?.item_subtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web
							? { '--content-background-image': 'url()' }
							: activeScreen?.background_image
							  ? {
										'--content-background-image': `url(${generateImageUrl(activeScreen.background_image?.file, {
											imageSizePreset: ImageSizePreset.Fullscreen,
											focalPoint: activeScreen.background_image.file?.focal_point
										})})`,
							    }
							  : undefined,
				}}
				footer={
					!loading && {
						component: (
							<>
								{showMapLayout && <AudioPlayerBarComponent bottomOffset={0} className="full-width" />}
								{!showMapLayout && (
									<TourStopFooter
										hasPrev={hasPrevious}
										onPrevClick={() => changeScreenAction('prev')}
										hasMap={hasMap}
										onMapClick={() => setShowModal(true)}
										keypadEnabled={keypadEnabled}
										qrEnabled={qrEnabled}
										onScanClick={toggleScannerModal}
										hasNext={hasNext}
										onNextClick={() => changeScreenAction('next')}
										onFinishClick={onTourExit}
										collectionType={collectionType}
									/>
								)}
							</>
						),
						props: { className: 'tour-stops-page footer' },
					}
				}
				dataItemId={String(collection.id)}
				dataItemType="collection"
				dataItemSubtype="tour"
				dataCy="page-collection-items"
			>
				{renderPageContent(isEmpty)}
			</Page>
			{!loading && !showMapLayout && <AudioPlayerBarComponent />}

			{hasMap && renderModal()}
		</>
	)
}

const CollectionItemsComponent = Container.default(withRouter(CollectionItems))
export default CollectionItemsComponent
